<template>
  <div class="a11y-modal">
    <div class="title">
      <h2>Invite User</h2>
      <button class="close" @click="closeModal">×</button>
    </div>

    <div class="content">
      <form id="inviteUser" novalidate @submit.prevent="submit">
        <div class="row">
          <div class="col-12 mb-4">
            <form-component>
              <input-text
                id="email"
                v-model="email"
                v-validate="'required|email|server:email'"
                data-vv-as="email"
                :error="errors.first('email')"
                name="email"
                label="User Email Address"
                type="text"
                placeholder="email@address.com"
                required
              />
            </form-component>
          </div>
        </div>
        <div class="row mb-4">
          <div class="col-12 col-sm-6">
            <form-component>
              <input-text
                id="firstName"
                v-model="firstName"
                v-validate="'required'"
                data-vv-as="first name"
                :error="errors.first('firstName')"
                name="firstName"
                label="First Name"
                type="text"
                placeholder="Sarah"
                required
              />
            </form-component>
          </div>
          <div class="col-12 col-sm-6">
            <form-component>
              <input-text
                id="lastName"
                v-model="lastName"
                v-validate="'required'"
                data-vv-as="last name"
                :error="errors.first('lastName')"
                name="lastName"
                label="Last Name"
                placeholder="Smith"
                type="text"
                required
              />
            </form-component>
          </div>
        </div>
        <div class="row">
          <div class="col-12 col-sm-6">
            <form-component>
              <input-text
                id="individualAllowances"
                v-model="individualAllowances"
                v-validate="'required|integer|min_value:0'"
                data-vv-as="individual allowances"
                :error="errors.first('individualAllowances')"
                name="individualAllowances"
                label="Individual Allowances"
                placeholder="0"
                type="number"
                :min="0"
                required
              />
            </form-component>
          </div>
          <div class="col-12 col-sm-6">
            <form-component>
              <input-text
                id="workshopAllowances"
                v-model="workshopAllowances"
                v-validate="'required|integer|min_value:0'"
                data-vv-as="wellbeing workshop allowances"
                :error="errors.first('workshopAllowances')"
                name="workshopAllowances"
                label="Wellbeing Workshop Allowances"
                placeholder="0"
                type="number"
                :min="0"
                required
              />
            </form-component>
          </div>
        </div>
        <div class="row">
          <div class="col-12 mb-4">
            <form-component>
              <input-text-area
                id="message"
                v-model="message"
                data-vv-as="message"
                :error="errors.first('message')"
                name="message"
                label="Custom Message (optional)"
                type="text"
                maxlength="2000"
              />
            </form-component>
          </div>
        </div>
      </form>
    </div>

    <div class="actions">
      <button type="button" @click="closeModal">Cancel</button>
      <button
        class="primary"
        type="submit"
        form="inviteUser"
        :disabled="submitting"
        :aria-disabled="submitting"
      >
        <font-awesome-icon
          v-if="submitting"
          :spin="true"
          :icon="['fas', 'spinner']"
          class="icon alt"
        />
        <span v-else>Send</span>
      </button>
    </div>
  </div>
</template>

<script>
import CampusClient from '@/api/CampusClient';
import FormComponent from '@/components/FormComponent.vue';
import InputText from '@/components/inputs/InputText.vue';
import InputTextArea from '@/components/inputs/InputTextArea.vue';
import ServerValidator from '@/helpers/mixins/server-validator';

export default {
  name: 'InviteStudentModal',

  components: {
    FormComponent,
    InputText,
    InputTextArea,
  },

  mixins: [ServerValidator],
  inject: ['collegeDetailsReport'],

  props: {
    initialEmail: {
      type: String,
      required: false,
      default: ''
    }
  },

  data() {
    return {
      client: new CampusClient(this.$http),
      submitting: false,
      email: '',
      firstName: '',
      lastName: '',
      individualAllowances: '',
      workshopAllowances: '',
      message: '',
    };
  },

  mounted() {
    this.email = this.initialEmail;
  },

  methods: {
    closeModal() {
      this.$modal.close();
      this.email = '';
      this.firstName = '';
      this.lastName = '';
      this.individualAllowances = '';
      this.workshopAllowances = '';
      this.message = '';
    },

    async submit() {
      this.$serverValidator.clear();

      const valid = await this.$validator.validateAll();
      if (!valid) {
        return;
      }

      this.submitting = true;
      try {
        await this.client.inviteStudent(
          this.collegeDetailsReport.schoolId,
          this.email,
          this.firstName,
          this.lastName,
          this.message,
          this.individualAllowances,
          this.workshopAllowances
        );
        this.$store.commit('setGlobalAlert', {
          msg: 'An invite was sent to the specified email address!'
        });
      } catch (e) {
        if (e.status === 400 && e.body.errors) {
          const errors = e.body.errors;
          let message = 'Received the following errors:';
          for(const key in errors) {
            if(!errors[key].length)
              continue;
            message += ` ${key} ${errors[key][0].toLowerCase()}`
            for(let i = 1; i < errors[key].length; i++)
              message += `, ${key} ${errors[key][i].toLowerCase()}`
            if(message[message.length-1] != '.')
              message += '.';
          }

          this.$store.commit('setGlobalAlert', { msg: message, error: true });

        } else {
          this.$store.commit('setGlobalAlert', {
            msg: 'There was an error inviting the specified user. Please try again!',
            error: true
          });
        }
      } finally {
        this.submitting = false;
        this.closeModal();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.a11y-modal {
  ::v-deep .form .form__input {
    label {
      text-transform: none;
      font-size: 15px;
      letter-spacing: 0;
      margin-bottom: 0.5rem;
      font-weight: 500;
    }
    textarea, input {
      border: solid 1px #d1d1d1;
      background: #fff;
      color: inherit;

      &:focus {
        color: inherit;
        border: solid 1px $dark-blue;
        background: #f8f8f8;
      }

      &::placeholder {
        color: inherit;
      }
    }
  }
}
</style>
